import { GraduationCap, User, Box, Calendar } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';

export default function MicrosoftOverview({
  title,
  description,
  isAIOverview,
  lastUpdated = '10/30/2025',
  role,
  linkTo,
  children
}: {
  title: string;
  description: string;
  isAIOverview?: boolean;
  lastUpdated?: string;
  role: string;
  linkTo: string;
  children: ReactNode;
}) {
  const [showMore, setShowMore] = useState(false);

  return (
    <div className='rounded-b-2xl rounded-tl-2xl bg-white'>
      {/* Header Section */}
      <div className='bg-gradient-to-r from-blue-600 to-blue-800 px-6 pb-16 pt-10 text-white'>
        <div className='mx-auto max-w-6xl'>
          <div className='flex items-start justify-between'>
            <div>
              <div className='mb-4 flex items-center gap-2'>
                <GraduationCap className='h-8 w-8' />
                <span className='text-sm font-semibold uppercase tracking-wider'>
                  Certification
                </span>
              </div>
              <h2 className='mb-4 text-2xl font-bold md:text-3xl'>
                Microsoft Certified: {title}
              </h2>
              <p className='max-w-2xl text-lg text-blue-100'>{description}</p>
            </div>
            <div className='hidden md:block'>
              <div className='flex h-32 w-32 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm'>
                <div className='flex h-24 w-24 items-center justify-center rounded-full bg-blue-400'>
                  <GraduationCap className='h-12 w-12 text-white' />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* At a Glance Section */}
      <div className='border-b border-gray-200 bg-gray-50'>
        <div className='mx-auto max-w-6xl px-6 py-8'>
          <h2 className='mb-6 text-xl font-semibold text-gray-900'>
            At a glance
          </h2>
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4'>
            <div className='flex items-start gap-3'>
              <GraduationCap className='mt-1 h-5 w-5 text-blue-600' />
              <div>
                <div className='text-sm font-medium text-gray-600'>Level</div>
                <div className='text-base font-semibold text-blue-600'>
                  Beginner
                </div>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <User className='mt-1 h-5 w-5 text-blue-600' />
              <div>
                <div className='text-sm font-medium text-gray-600'>Role</div>
                <div className='text-base font-semibold text-blue-600'>
                  {role}
                </div>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Box className='mt-1 h-5 w-5 text-blue-600' />
              <div>
                <div className='text-sm font-medium text-gray-600'>Product</div>
                <div className='text-base font-semibold text-blue-600'>
                  Azure
                </div>
              </div>
            </div>
            <div className='flex items-start gap-3'>
              <Calendar className='mt-1 h-5 w-5 text-blue-600' />
              <div>
                <div className='text-sm font-medium text-gray-600'>
                  Last Updated
                </div>
                <div className='text-base font-semibold text-gray-900'>
                  {lastUpdated}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='mx-auto max-w-6xl px-6 py-8'>
        {/* Jump to Section */}
        <div className='mb-12'>
          <h3 className='mb-4 text-lg font-semibold text-gray-900'>Jump to</h3>
          <div className='space-y-2'>
            <Link
              target='_blank'
              to={linkTo}
              className='block text-blue-600 hover:text-blue-800 hover:underline'
            >
              Prepare for the exam
            </Link>
            <Link
              target='_blank'
              to={linkTo}
              className='block text-blue-600 hover:text-blue-800 hover:underline'
            >
              Practice for the exam
            </Link>
            <Link
              target='_blank'
              to={linkTo}
              className='block text-blue-600 hover:text-blue-800 hover:underline'
            >
              Take the exam
            </Link>
            {showMore && (
              <Link
                target='_blank'
                to={linkTo}
                className='block text-blue-600 hover:text-blue-800 hover:underline'
              >
                Certification Resources
              </Link>
            )}
          </div>
          <button
            onClick={() => setShowMore(!showMore)}
            className='mt-3 font-medium text-blue-600 hover:text-blue-800'
          >
            {showMore ? 'Show less' : 'Show more'}
          </button>
        </div>

        {/* Overview Section */}
        <div>
          <h2 className='mb-6 text-3xl font-bold text-gray-900'>Overview</h2>

          {children}
        </div>

        {/* Action Sections */}
        <div className='mt-16 space-y-8'>
          <Link
            className='underline hover:text-primary'
            target='_blank'
            to={linkTo}
          >
            Learn more about Microsoft Certified: {title}
          </Link>
        </div>
      </div>
    </div>
  );
}
